import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import Slide from "@mui/material/Slide";
import useMediaQuery from "@mui/material/useMediaQuery";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { notificationsApi } from "../api/endpoints";
import { createNotificationTracker, startVisiblePolling } from "../utils/liveUpdates";

export function LiveNotifications({ onUnreadChange }) {
  const { t } = useTranslation();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [queue, setQueue] = useState([]);
  useEffect(() => {
    let track = createNotificationTracker();
    const resetPreferences = () => { track = createNotificationTracker(); setQueue([]); };
    window.addEventListener("atlas:notification-preferences", resetPreferences);
    const stop = startVisiblePolling(async (active) => {
      const data = await notificationsApi.list();
      if (!active()) return;
      onUnreadChange(data.unreadCount ?? 0);
      const fresh = track(data.notifications);
      if (fresh.length) {
        setQueue((previous) => [...previous, ...fresh]);
        window.dispatchEvent(new Event("atlas:live-update"));
      }
    });
    return () => { stop(); window.removeEventListener("atlas:notification-preferences", resetPreferences); };
  }, [onUnreadChange]);

  const notification = queue[0];
  const dismiss = () => setQueue((previous) => previous.slice(1));
  const path = notification?.linkPath;
  const destination = typeof path === "string" && path.startsWith("/") && !path.startsWith("//") && !path.includes("\\")
    ? path : "/notificaciones";
  return (
    <Snackbar
      key={notification?.id ?? "empty"}
      open={Boolean(notification)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      slots={{ transition: Slide }}
      slotProps={{ transition: { direction: "left" } }}
      transitionDuration={reducedMotion ? 0 : { enter: 350, exit: 250 }}
      autoHideDuration={8000}
      onClose={(_, reason) => { if (reason !== "clickaway") dismiss(); }}
      sx={{ top: { xs: "calc(64px + env(safe-area-inset-top))", sm: 72 }, maxWidth: 480 }}
    >
      <Alert severity="info" variant="filled" sx={{ width: "100%", boxShadow: 6 }} action={
        <Stack direction="row" alignItems="center">
          <Button color="inherit" size="small" component={RouterLink} to={destination} onClick={dismiss}>
            {t("common.view", { defaultValue: "Ver" })}
          </Button>
          <IconButton color="inherit" size="small" onClick={dismiss} aria-label={t("common.close", { defaultValue: "Cerrar" })}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      }>
        <AlertTitle>{notification ? t(notification.titleKey, { ...notification.params, defaultValue: notification.titleKey }) : ""}</AlertTitle>
        {notification?.bodyKey ? t(notification.bodyKey, { ...notification.params, defaultValue: notification.bodyKey }) : ""}
      </Alert>
    </Snackbar>
  );
}
